/**
 * Pure helpers for tournament team pairing (random uniform + constrained greedy matching).
 */

export const PAIRING_MODES = {
  MANUAL: 'manual',
  RANDOM_UNIFORM: 'random_uniform',
  RANDOM_CONSTRAINED: 'random_constrained'
};

/** Fisher–Yates shuffle in place; optional RNG for tests */
export function shuffleInPlace(arr, rng = Math.random) {
  const a = arr;
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * @param {string[]} playerIds
 * @param {() => number} [rng]
 * @returns {{ ok: true, pairs: [string, string][] } | { ok: false, error: string }}
 */
export function buildUniformRandomPairs(playerIds, rng = Math.random) {
  const ids = [...new Set(playerIds)].filter(Boolean);
  if (ids.length !== playerIds.length) {
    return { ok: false, error: 'Duplicate player IDs are not allowed.' };
  }
  if (ids.length % 2 !== 0) {
    return { ok: false, error: 'Need an even number of players to form pairs.' };
  }
  shuffleInPlace(ids, rng);
  const pairs = [];
  for (let i = 0; i < ids.length; i += 2) {
    pairs.push([ids[i], ids[i + 1]]);
  }
  return { ok: true, pairs };
}

/**
 * Random disjoint pairs; if count is odd, one player is left unpaired (stays in solo pool).
 * @returns {{ ok: true, pairs: [string, string][], leftoverId: string|null } | {  ok: false, error: string, pairs: [], leftoverId: null }}
 */
export function buildUniformRandomPairsAllowOdd(playerIds, rng = Math.random) {
  const ids = [...new Set(playerIds)].filter(Boolean);
  if (ids.length !== playerIds.length) {
    return { ok: false, error: 'Duplicate player IDs are not allowed.', pairs: [], leftoverId: null };
  }
  if (ids.length === 0) {
    return { ok: true, pairs: [], leftoverId: null };
  }
  const shuffled = [...ids];
  shuffleInPlace(shuffled, rng);
  const pairs = [];
  for (let i = 0; i + 1 < shuffled.length; i += 2) {
    pairs.push([shuffled[i], shuffled[i + 1]]);
  }
  const leftoverId = shuffled.length % 2 === 1 ? shuffled[shuffled.length - 1] : null;
  return { ok: true, pairs, leftoverId };
}

const normBand = (s) => String(s || '').trim();

/**
 * @param {string[][]} forbiddenPairs — e.g. [['A','B'], ['A','A']]
 * @returns {Set<string>} keys "a||b" with a <= b lexicographically
 */
export function normalizeForbiddenBandPairs(forbiddenPairs) {
  const set = new Set();
  if (!Array.isArray(forbiddenPairs)) return set;
  for (const row of forbiddenPairs) {
    if (!Array.isArray(row) || row.length !== 2) continue;
    const a = normBand(row[0]);
    const b = normBand(row[1]);
    if (!a || !b) continue;
    const [x, y] = a <= b ? [a, b] : [b, a];
    set.add(`${x}||${y}`);
  }
  return set;
}

/**
 * @param {string} b1
 * @param {string} b2
 * @param {Set<string>} forbiddenNorm
 */
export function isBandPairForbidden(b1, b2, forbiddenNorm) {
  const x = normBand(b1);
  const y = normBand(b2);
  if (!x || !y) return false;
  const [lo, hi] = x <= y ? [x, y] : [y, x];
  return forbiddenNorm.has(`${lo}||${hi}`);
}

/**
 * @param {string} a
 * @param {string} b
 * @param {Set<string>} seedSet
 * @param {Record<string, string>} bandByPlayerId
 * @param {Set<string>} forbiddenBandNorm
 * @param {{ requireBands?: boolean }} opts
 */
export function isPairingEdgeAllowed(a, b, seedSet, bandByPlayerId, forbiddenBandNorm, opts = {}) {
  if (a === b) return false;
  if (seedSet.has(a) && seedSet.has(b)) return false;
  const ba = bandByPlayerId[a];
  const bb = bandByPlayerId[b];
  if (opts.requireBands && (!normBand(ba) || !normBand(bb))) {
    return false;
  }
  if (normBand(ba) && normBand(bb) && isBandPairForbidden(ba, bb, forbiddenBandNorm)) {
    return false;
  }
  return true;
}

/**
 * All unordered allowed edges, then shuffled.
 * @param {string[]} playerIds
 * @param {Iterable<string>} seedPlayerIds
 * @param {Record<string, string>} bandByPlayerId
 * @param {string[][]} forbiddenBandPairs
 * @param {() => number} [rng]
 * @param {{ requireBands?: boolean }} [opts]
 * @returns {[string, string][]}
 */
export function buildAllowedEdgesShuffled(
  playerIds,
  seedPlayerIds,
  bandByPlayerId,
  forbiddenBandPairs,
  rng = Math.random,
  opts = {}
) {
  const ids = [...new Set(playerIds)].filter(Boolean);
  const seedSet = new Set(seedPlayerIds);
  const forbiddenNorm = normalizeForbiddenBandPairs(forbiddenBandPairs);
  const edges = [];
  for (let i = 0; i < ids.length; i += 1) {
    for (let j = i + 1; j < ids.length; j += 1) {
      const a = ids[i];
      const b = ids[j];
      if (isPairingEdgeAllowed(a, b, seedSet, bandByPlayerId, forbiddenNorm, opts)) {
        edges.push([a, b]);
      }
    }
  }
  shuffleInPlace(edges, rng);
  return edges;
}

/**
 * Greedy maximal matching on a list of edges [a,b].
 * @returns {{ pairs: [string, string][], matched: Set<string>, unmatched: string[] }}
 */
export function greedyMatchingFromEdges(edges, allPlayerIds) {
  const matched = new Set();
  const pairs = [];
  for (const [a, b] of edges) {
    if (matched.has(a) || matched.has(b)) continue;
    pairs.push([a, b]);
    matched.add(a);
    matched.add(b);
  }
  const idSet = new Set(allPlayerIds);
  const unmatched = [...idSet].filter((id) => !matched.has(id));
  return { pairs, matched, unmatched };
}

/**
 * @param {string[]} playerIds
 * @param {Iterable<string>} seedPlayerIds
 * @param {Record<string, string>} bandByPlayerId
 * @param {string[][]} forbiddenBandPairs
 * @param {() => number} [rng]
 * @param {{ requireBands?: boolean }} [opts]
 */
export function buildConstrainedGreedyPairs(
  playerIds,
  seedPlayerIds,
  bandByPlayerId,
  forbiddenBandPairs,
  rng = Math.random,
  opts = {}
) {
  const ids = [...new Set(playerIds)].filter(Boolean);
  if (ids.length % 2 !== 0) {
    return {
      ok: false,
      error: 'Need an even number of players to form pairs.',
      pairs: [],
      unmatched: ids,
      unmatchedReasons: {}
    };
  }
  const edges = buildAllowedEdgesShuffled(ids, seedPlayerIds, bandByPlayerId, forbiddenBandPairs, rng, opts);
  const { pairs, unmatched } = greedyMatchingFromEdges(edges, ids);
  const unmatchedReasons = {};
  if (unmatched.length > 0) {
    const seedSet = new Set(seedPlayerIds);
    const forbiddenNorm = normalizeForbiddenBandPairs(forbiddenBandPairs);
    for (const u of unmatched) {
      const reasons = [];
      const others = ids.filter((x) => x !== u);
      let anyAllowed = false;
      for (const v of others) {
        if (isPairingEdgeAllowed(u, v, seedSet, bandByPlayerId, forbiddenNorm, opts)) {
          anyAllowed = true;
          break;
        }
      }
      if (!anyAllowed) {
        reasons.push('No valid partner under current seed and band rules.');
      } else {
        reasons.push('Could not fit everyone in one round of pairs; try again or relax rules.');
      }
      unmatchedReasons[u] = reasons;
    }
  }
  return {
    ok: unmatched.length === 0,
    pairs,
    unmatched,
    unmatchedReasons,
    error: unmatched.length > 0 ? 'Some players could not be paired under the current rules.' : null
  };
}

const ELO_KEYS = ['mixed_elo_rating', 'levels_elo_rating', 'singles_elo_rating'];

/**
 * @param {string[]} playerIds
 * @param {Array<{ player_id: string, mixed_elo_rating?: number, levels_elo_rating?: number, singles_elo_rating?: number }>} eloRows
 * @param {number} topN
 * @param {'mixed_elo_rating'|'levels_elo_rating'|'singles_elo_rating'} eloKey
 * @returns {string[]} player ids (top N by elo among playerIds)
 */
export function suggestSeedPlayerIdsFromTopN(playerIds, eloRows, topN, eloKey = 'mixed_elo_rating') {
  const n = Math.max(0, Math.floor(Number(topN) || 0));
  if (n === 0 || !playerIds?.length) return [];
  const key = ELO_KEYS.includes(eloKey) ? eloKey : 'mixed_elo_rating';
  const want = new Set(playerIds);
  const byPlayer = new Map((eloRows || []).map((r) => [r.player_id, r]));
  const scored = playerIds.map((id) => {
    const row = byPlayer.get(id);
    const raw = row ? row[key] : null;
    const v = raw == null || Number.isNaN(Number(raw)) ? -Infinity : Number(raw);
    return { id, v };
  });
  scored.sort((a, b) => b.v - a.v);
  return scored.slice(0, n).map((s) => s.id);
}

/**
 * Collect distinct non-empty band codes from a record
 * @param {Record<string, string>} bandByPlayerId
 * @returns {string[]}
 */
export function distinctBandCodes(bandByPlayerId) {
  const set = new Set();
  Object.values(bandByPlayerId || {}).forEach((c) => {
    const n = normBand(c);
    if (n) set.add(n);
  });
  return [...set].sort((a, b) => a.localeCompare(b));
}
